import { useCallback, useEffect, useRef, useState } from 'react';
import { Filters, ModelCorrelation } from '../types';

interface UseCorrelationSelectionParams {
  filters: Filters;
  /** The rows a user is allowed to tick — visible and not hidden. */
  selectable: ModelCorrelation[];
}

export default function useCorrelationSelection({
  filters, selectable,
}: UseCorrelationSelectionParams) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);

  const selectedVisible = selectable.filter(correlation => selectedIds.has(correlation.id));
  const allSelected = selectable.length > 0 && selectedVisible.length === selectable.length;
  const someSelected = selectedVisible.length > 0 && !allSelected;

  // Stable identity keeps React.memo on CorrelationRow effective while the user types.
  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      selectable.forEach(({ id }) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const dropIds = (ids: number[]) => {
    setSelectedIds(current => new Set([...current].filter(id => !ids.includes(id))));
  };

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  // A filter or search change drops the whole selection rather than leaving rows selected off-screen.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters]);

  // Rows also leave the selectable set without a filter change — a single remove soft-hides an EdGate
  // row, "Show less" collapses the list — and a stale id renders as a checked, un-untickable box.
  useEffect(() => {
    setSelectedIds((current) => {
      const selectableIds = new Set(selectable.map(({ id }) => id));
      const next = new Set([...current].filter(id => selectableIds.has(id)));

      return next.size === current.size ? current : next;
    });
  }, [selectable]);

  return {
    allSelected,
    clearSelection,
    dropIds,
    selectAllRef,
    selectedIds,
    selectedVisible,
    toggleSelect,
    toggleSelectAll,
  };
}
